import { useCallback, useEffect, useRef, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
import { api, type BadgeBoard } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Page, Progress } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Trace, TraceRegion } from '../lib/trace'
import { Mascot } from '../components/Mascot'
import { sfx } from '../lib/sound'
import { useViewport } from '../lib/screen'

/**
 * The trophy room, in 3D.
 *
 * Sam asked (8 Oct 2026) to keep the flat trophy room exactly as it is and
 * add a second one a child can arrange: a small room seen from above, like a
 * doll's house with the front and one side taken off, where each trophy can
 * be picked up and put down somewhere else. The flat one (Badges.tsx) is
 * still there, one button away, and is the one to use without WebGL or with
 * a screen reader.
 *
 * **What is in the room.** Each trophy a child has found is a shiny gold cup
 * with the badge's picture on its front and its name on a little plaque. The
 * ones they are working towards stand on their own "Coming soon" shelf on the
 * side wall, pale silver, with how far along they are ("3/10"). They cannot
 * be moved: they are not the child's yet. Hidden ones are one wrapped present.
 *
 * **Moving a trophy.** Press on a gold cup and drag. It lifts, glows, and
 * follows the finger across the floor, the rug, the shelves, the window sill
 * and the two pedestals, always standing on whatever is under it. Let go and
 * it settles there. If another trophy is already standing on that exact spot
 * it shuffles to the nearest free space rather than sinking into it. Dragging
 * anywhere else turns the room a little; the angles are limited so the camera
 * never goes under the floor or behind a wall.
 *
 * **Nothing disappears or shrinks (D-048).** Every trophy the child has found
 * is always somewhere in the room. A saved spot that no longer makes sense
 * (say the furniture moved in a later version) is ignored and the trophy goes
 * to a free shelf spot instead. There is no bin and no "remove".
 *
 * **Remembered per child.** Where each trophy stands is saved as one setting,
 * `trophy_layout`, holding `{ code: [x, y, z, turn] }`. It is saved a moment
 * (0.8 s) after the last move, not on every wiggle, and saves are sent one
 * after another so an older one can never land after a newer one. "Tidy up"
 * puts everything back on the shelves and saves an empty layout, which means
 * "use the tidy arrangement".
 *
 * **Calm.** No idle motion at all when the device asks for reduced motion;
 * otherwise only a few twinkles on the gold and clouds drifting past the
 * window. The picture is only redrawn when something changes or twinkles, and
 * shadows are only recalculated when a trophy moves, to spare tablet batteries.
 */

// ------------------------------------------------------------------- types

type Found = BadgeBoard['found'][number]
type Towards = BadgeBoard['working_towards'][number]

type Item =
  | ({ kind: 'found' } & Found)
  | ({ kind: 'towards' } & Towards)
  | { kind: 'hidden'; count: number }

/** Where one trophy stands: x, y (the top of what it stands on), z, turn. */
type Spot = [number, number, number, number]
type Layout = Record<string, Spot>

type SaveState = 'idle' | 'waiting' | 'saving' | 'saved' | 'failed'

const SETTING_KEY = 'trophy_layout'
const SAVE_AFTER_MS = 800

interface RoomInput {
  found: Found[]
  towards: Towards[]
  hiddenCount: number
  layout: Layout
  reducedMotion: boolean
}
interface RoomEvents {
  onSelect: (item: Item | null) => void
  onLayout: (json: string) => void
  onLost: () => void
}
interface RoomHandle {
  select: (code: string | null) => void
  turn: () => void
  tidy: () => void
  dispose: () => void
}

/** A saved layout, read defensively: anything odd is simply left out. */
function parseLayout(raw: string | null): Layout {
  if (!raw) return {}
  try {
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
    const out: Layout = {}
    for (const [code, spot] of Object.entries(value as Record<string, unknown>)) {
      if (Array.isArray(spot) && spot.length === 4
          && spot.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        out[code] = spot as Spot
      }
    }
    return out
  } catch {
    return {}
  }
}

// ================================================================= screen

export function TrophyRoom3D({
  learnerId, onHome, onFlat,
}: { learnerId: string; onHome: () => void; onFlat: () => void }) {
  const [board, setBoard] = useState<BadgeBoard | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [selected, setSelected] = useState<Item | null>(null)
  const [save, setSave] = useState<SaveState>('idle')
  const [confirmTidy, setConfirmTidy] = useState(false)
  const [glProblem, setGlProblem] = useState<null | 'none' | 'lost'>(null)
  const [attempt, setAttempt] = useState(0)
  const { narrow } = useViewport()

  const hostRef = useRef<HTMLDivElement>(null)
  const roomRef = useRef<RoomHandle | null>(null)
  const layoutRef = useRef<Layout>({})

  // ---- loading: the badges and the saved arrangement, together
  const load = () => {
    setError(null)
    Promise.all([api.badges(learnerId), api.learnerSetting(learnerId, SETTING_KEY)])
      .then(([b, raw]) => {
        layoutRef.current = parseLayout(raw)
        setBoard(b)
      })
      .catch(setError)
  }
  useEffect(load, [learnerId])

  // ---- saving: a moment after the last move, one save at a time
  const aliveRef = useRef(true)
  const pendingRef = useRef<string | null>(null)
  const timerRef = useRef<number | undefined>(undefined)
  const chainRef = useRef<Promise<unknown>>(Promise.resolve())

  const flush = useCallback(() => {
    window.clearTimeout(timerRef.current)
    const json = pendingRef.current
    if (json === null) return
    pendingRef.current = null
    if (aliveRef.current) setSave('saving')
    chainRef.current = chainRef.current
      .then(() => api.setLearnerSetting(learnerId, SETTING_KEY, json))
      .then(() => {
        if (aliveRef.current && pendingRef.current === null) setSave('saved')
      })
      .catch(() => {
        // Keep it for the next try, unless something newer is already waiting.
        if (pendingRef.current === null) pendingRef.current = json
        if (aliveRef.current) setSave('failed')
      })
  }, [learnerId])

  const queueSave = useCallback((json: string) => {
    layoutRef.current = parseLayout(json)
    pendingRef.current = json
    setSave('waiting')
    window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(flush, SAVE_AFTER_MS)
  }, [flush])

  // Leaving the room straight after a move still saves it.
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      flush()
    }
  }, [flush])

  // ---- the 3D room itself
  const eventsRef = useRef<RoomEvents>({ onSelect: () => {}, onLayout: () => {}, onLost: () => {} })
  eventsRef.current = {
    onSelect: (item) => setSelected(item),
    onLayout: queueSave,
    onLost: () => setGlProblem('lost'),
  }

  useEffect(() => {
    const host = hostRef.current
    if (!board || !host || glProblem) return
    let cancelled = false
    let room: RoomHandle | null = null
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    fontsReady().then(() => {
      if (cancelled) return
      if (!webglAvailable()) { setGlProblem('none'); return }
      try {
        room = createRoom(host, {
          found: board.found,
          towards: board.working_towards,
          hiddenCount: board.hidden_count,
          layout: layoutRef.current,
          reducedMotion,
        }, {
          onSelect: (item) => eventsRef.current.onSelect(item),
          onLayout: (json) => eventsRef.current.onLayout(json),
          onLost: () => eventsRef.current.onLost(),
        })
        roomRef.current = room
      } catch {
        setGlProblem('none')
      }
    })
    return () => {
      cancelled = true
      room?.dispose()
      roomRef.current = null
      setSelected(null)
    }
  }, [board, glProblem, attempt])

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!board) return <Loading />

  const found = board.found.length
  const title = `${board.display_name}'s trophy room`
  const flatButton = (
    <Button tone="ghost" onClick={onFlat} style={{
      padding: '8px 14px', color: theme.orangeDark, borderColor: theme.orangeTint, background: theme.card,
    }}>Flat view</Button>
  )
  const titleStyle = {
    margin: 0, fontSize: narrow ? 24 : 28, color: theme.text, lineHeight: 1.15, textAlign: 'center' as const,
  }

  return (
    <Page width={1000} style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 0px))' }}>
      {/* ------------------------------------------------------- top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ExitButton onExit={onHome} />
        <div style={{ width: 96, flexShrink: 0 }} aria-hidden />
        {narrow ? <div style={{ flex: 1 }} /> : <h1 style={{ ...titleStyle, flex: 1 }}>{title}</h1>}
        {flatButton}
      </div>
      {narrow ? <h1 style={{ ...titleStyle, marginTop: 10 }}>{title}</h1> : null}
      <p style={{ margin: '6px 0 10px', textAlign: 'center', fontSize: 15, color: theme.muted }}>
        {glProblem ? 'Your trophies are all safe.' : 'Drag a trophy to move it.'}
      </p>

      {glProblem ? (
        <NoRoom lost={glProblem === 'lost'} onFlat={onFlat}
                onRetry={() => { setGlProblem(null); setAttempt((n) => n + 1) }} />
      ) : (
        <TraceRegion id="T19" note="earned trophies, joined to T18 badge; each stands where the child put it">
          <div style={{
            position: 'relative',
            height: narrow ? 'min(60vh, 520px)' : 'min(66vh, 640px, 62vw)',
            minHeight: 320,
            borderRadius: 22,
            overflow: 'hidden',
            border: `3px solid ${theme.borderWarm}`,
            background: 'radial-gradient(ellipse at 45% 30%, #FFF9EF 0%, #FCEBD5 65%, #F5DDBF 100%)',
            boxShadow: '0 6px 18px rgba(80,50,20,0.12)',
          }}>
            <div ref={hostRef} style={{ position: 'absolute', inset: 0 }} />

            {/* tidy up, top right */}
            <button type="button" onClick={() => { sfx.tap(); setConfirmTidy(true) }} style={{
              ...pillButton, position: 'absolute', top: 10, right: 10,
            }}>🧹 Tidy up</button>

            {/* saved or not, bottom left */}
            <SaveStatus state={save} onRetry={flush} />

            {/* turn the chosen trophy, bottom centre */}
            {selected?.kind === 'found' ? (
              <button type="button" onClick={() => roomRef.current?.turn()} style={{
                ...pillButton, position: 'absolute', bottom: 12, right: 12,
                background: theme.orange, color: '#FFFFFF', border: 'none', padding: '0 22px',
                boxShadow: '0 3px 0 rgba(163,87,27,0.45)',
              }} aria-label={`Turn ${selected.name}`}>↻ Turn</button>
            ) : null}

            {confirmTidy ? (
              <TidyConfirm
                onYes={() => { setConfirmTidy(false); sfx.tap(); roomRef.current?.tidy() }}
                onNo={() => setConfirmTidy(false)}
              />
            ) : null}
          </div>
        </TraceRegion>
      )}

      {/* ----------------------------------------------- what was tapped */}
      {selected && !glProblem ? (
        <Details item={selected} onClose={() => { roomRef.current?.select(null); setSelected(null) }} />
      ) : glProblem ? null : (
        <p style={{ textAlign: 'center', color: theme.faint, fontSize: 14, margin: '12px 0 0' }}>
          Tap a trophy to see what it is for. Drag the room to look around.
        </p>
      )}

      {found === 0 ? (
        <p style={{ textAlign: 'center', color: theme.text2, fontSize: 16, margin: '14px 0 0' }}>
          The shelves are waiting. Finish a session and the first trophy arrives.
        </p>
      ) : null}

      {board.sibling_news.length ? (
        <p style={{
          margin: '18px auto 0', maxWidth: 640, padding: '12px 14px', background: theme.blueFaint,
          borderRadius: radius.md, fontSize: 15, color: theme.text2, lineHeight: 1.5,
        }}>
          {board.sibling_news[0]!.display_name} found{' '}
          <Trace id="T18.name"><strong>{board.sibling_news[0]!.name}</strong></Trace>{' '}
          {board.sibling_news[0]!.emoji}. Nice one.
        </p>
      ) : null}
    </Page>
  )
}

const pillButton = {
  minHeight: TAP_MIN, padding: '0 14px', borderRadius: 999, cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 15, fontWeight: 700,
  background: 'rgba(255,255,255,0.92)', color: '#A3571B',
  border: '1.5px solid #FDEBD8', boxShadow: '0 2px 6px rgba(80,50,20,0.12)',
} as const

function SaveStatus({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state === 'idle') return null
  const text = state === 'saved' ? 'Saved ✓'
    : state === 'failed' ? 'Not saved. Tap to try again.'
    : 'Saving…'
  const style = {
    position: 'absolute' as const, left: 10, bottom: 12, fontSize: 13, fontWeight: 700,
    padding: '6px 12px', borderRadius: 999,
    background: 'rgba(255,255,255,0.88)',
    color: state === 'failed' ? theme.orangeDark : state === 'saved' ? theme.greenDark : theme.muted,
    border: 'none', fontFamily: 'inherit',
  }
  const label = (
    <Trace id="T20.value" writes title="setting trophy_layout: where each trophy stands, saved per child">
      {text}
    </Trace>
  )
  return state === 'failed'
    ? <button type="button" onClick={onRetry} style={{ ...style, cursor: 'pointer', minHeight: 32 }}>{label}</button>
    : <div role="status" style={style}>{label}</div>
}

function TidyConfirm({ onYes, onNo }: { onYes: () => void; onNo: () => void }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(60,40,20,0.25)',
      display: 'grid', placeItems: 'center', padding: 16,
    }}>
      <div role="dialog" aria-label="Tidy up" style={{
        background: theme.card, borderRadius: radius.xl, padding: '18px 18px 16px', maxWidth: 340,
        textAlign: 'center', boxShadow: '0 10px 30px rgba(60,40,20,0.25)',
      }}>
        <div style={{ fontSize: 30, marginBottom: 6 }}>🧹</div>
        <h3 style={{ margin: '0 0 6px', fontSize: 20, color: theme.text }}>Tidy up?</h3>
        <p style={{ margin: '0 0 14px', fontSize: 15, color: theme.text2, lineHeight: 1.45 }}>
          Every trophy goes back on the shelves, in the order you found them.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button tone="child" onClick={onYes}>Yes, tidy up</Button>
          <Button tone="ghost" onClick={onNo}>No, keep it</Button>
        </div>
      </div>
    </div>
  )
}

function NoRoom({ lost, onFlat, onRetry }: { lost: boolean; onFlat: () => void; onRetry: () => void }) {
  return (
    <Card style={{ textAlign: 'center', maxWidth: 460, margin: '20px auto 0' }}>
      <div style={{ display: 'grid', placeItems: 'center', marginBottom: 8 }}>
        <Mascot size={76} mood="wonder" label="" />
      </div>
      <h2 style={{ margin: '0 0 8px', fontSize: 22, color: theme.text }}>
        {lost ? 'The 3D room took a nap' : 'This screen can’t show the 3D room'}
      </h2>
      <p style={{ margin: '0 0 18px', color: theme.muted, fontSize: 16, lineHeight: 1.5 }}>
        Every trophy is still on its shelf in the flat view.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button tone="child" onClick={onFlat}>Flat view</Button>
        {lost ? <Button tone="ghost" onClick={onRetry}>Try again</Button> : null}
      </div>
    </Card>
  )
}

function Details({ item, onClose }: { item: Item; onClose: () => void }) {
  return (
    <div role="status" style={{
      margin: '14px auto 0', maxWidth: 640, background: theme.card, border: `1px solid ${theme.borderWarm}`,
      borderRadius: radius.xl, padding: 16, display: 'flex', gap: 14, alignItems: 'flex-start',
    }}>
      <div style={{ fontSize: 34, lineHeight: 1 }}>
        {item.kind === 'hidden' ? '🎁' : <Trace id="T18.emoji">{item.emoji}</Trace>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.kind === 'hidden' ? (
          <>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, color: theme.text }}>
              {item.count === 1 ? 'Still a surprise' : `${item.count} surprises`}
            </h3>
            <p style={{ margin: 0, fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>
              Some trophies come from what you do. Some just turn up.
            </p>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, color: theme.text }}>
              <Trace id="T18.name">{item.name}</Trace>
            </h3>
            <p style={{ margin: '0 0 8px', fontSize: 15, color: theme.text2, lineHeight: 1.5 }}>
              <Trace id="T18.description">{item.description}</Trace>
            </p>
            {item.kind === 'found' ? (
              <p style={{ margin: 0, fontSize: 14, color: theme.greenDark, fontWeight: 700 }}>
                In your room since{' '}
                <Trace id="T19.awarded_at">{new Date(item.awarded_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</Trace>
              </p>
            ) : (
              <>
                <div style={{ fontSize: 14, color: theme.muted, marginBottom: 6 }}>
                  <Trace id="calc.counted from T14 trials and T12 sessions against T18 criterion_json">
                    {item.now} of {item.target} {item.unit}
                  </Trace>
                </div>
                <Progress done={item.now} total={item.target} />
              </>
            )}
          </>
        )}
      </div>
      <button type="button" onClick={onClose} aria-label="Close" style={{
        background: 'transparent', border: 'none', fontSize: 22, color: theme.faint,
        cursor: 'pointer', minWidth: TAP_MIN, minHeight: TAP_MIN,
      }}>×</button>
    </div>
  )
}

// ================================================================ helpers

/** Canvas text needs the real fonts loaded first, or labels draw in Times. */
async function fontsReady(): Promise<void> {
  try {
    await Promise.race([
      Promise.all([document.fonts.load('600 40px Fredoka'), document.fonts.load('700 40px Nunito')]),
      new Promise((resolve) => window.setTimeout(resolve, 1500)),
    ])
  } catch {
    /* draw with whatever font there is */
  }
}

function webglAvailable(): boolean {
  try {
    const c = document.createElement('canvas')
    const gl = c.getContext('webgl2') ?? c.getContext('webgl')
    if (!gl) return false
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

const LABEL_FONT = "Fredoka, Nunito, 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif"
const EMOJI_FONT = "'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif"

function canvas2d(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  const g = c.getContext('2d')
  if (!g) throw new Error('no 2d canvas')
  return [c, g]
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath()
  g.moveTo(x + r, y)
  g.arcTo(x + w, y, x + w, y + h, r)
  g.arcTo(x + w, y + h, x, y + h, r)
  g.arcTo(x, y + h, x, y, r)
  g.arcTo(x, y, x + w, y, r)
  g.closePath()
}

const easeOut = (k: number) => 1 - (1 - k) ** 3
const easeInOut = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2)

// ============================================================== the room

/**
 * Something a trophy can stand on. Rectangles and circles, seen from above,
 * with the height of their top. `catchers` are the meshes a pointer hits to
 * mean "put it here": the thing itself, plus an invisible box above shelves
 * and pedestals, so pointing where the trophy would stand counts too.
 */
interface Surface {
  key: string
  top: number
  shape: 'rect' | 'circle'
  cx: number
  cz: number
  hw: number
  hd: number
  r: number
  floor: boolean
}

interface Trophy {
  code: string
  item: Item
  group: THREE.Group
  shiny: THREE.Mesh[]
  surface: Surface
  x: number
  z: number
  rot: number
}

interface Entry {
  key: string
  item: Item
  group: THREE.Group
  trophy: Trophy | null
  height: number
}

const LIFT = 0.18 // how far a trophy rises while carried
const GAP = 0.56 // closest two trophies' centres may stand
const EDGE = 0.2 // how close to an edge a trophy's centre may stand
const SHELF_STEP = 0.76
const PER_SHELF = 7
const TROPHY_SCALE = 1.12

function createRoom(host: HTMLElement, input: RoomInput, events: RoomEvents): RoomHandle {
  const { reducedMotion } = input
  const disposables: { dispose: () => void }[] = []
  const track = <T extends { dispose: () => void }>(x: T): T => {
    disposables.push(x)
    return x
  }

  // ------------------------------------------------------------ renderer
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
  renderer.setClearColor(0x000000, 0)
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.NeutralToneMapping
  renderer.toneMappingExposure = 1.0
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.shadowMap.autoUpdate = false
  renderer.shadowMap.needsUpdate = true
  const canvas = renderer.domElement
  Object.assign(canvas.style, { display: 'block', width: '100%', height: '100%', touchAction: 'none' })
  canvas.setAttribute('role', 'img')
  canvas.setAttribute('aria-label', 'A trophy room you can arrange by dragging the trophies')
  host.appendChild(canvas)
  const aniso = Math.min(8, renderer.capabilities.getMaxAnisotropy())

  const scene = new THREE.Scene()
  const pmrem = new THREE.PMREMGenerator(renderer)
  const envScene = new RoomEnvironment()
  const envMap = track(pmrem.fromScene(envScene, 0.04).texture)
  envScene.dispose()
  pmrem.dispose()
  scene.environment = envMap
  scene.environmentIntensity = 0.55

  const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100)

  // ------------------------------------------------------------- lights
  scene.add(new THREE.HemisphereLight('#FFF3E0', '#C69C68', 1.1))
  const sun = new THREE.DirectionalLight('#FFF1DC', 2.0)
  sun.position.set(4.5, 9, 7)
  sun.target.position.set(0, 0, -1.2)
  sun.castShadow = true
  sun.shadow.mapSize.set(2048, 2048)
  Object.assign(sun.shadow.camera, { left: -8, right: 8, top: 8, bottom: -8, near: 1, far: 30 })
  sun.shadow.bias = -0.0004
  sun.shadow.normalBias = 0.02
  scene.add(sun, sun.target)
  const fill = new THREE.DirectionalLight('#FFE9D0', 0.6)
  fill.position.set(-6, 5, 6)
  scene.add(fill)

  // ------------------------------------------------------- materials
  const std = (color: string, o: THREE.MeshStandardMaterialParameters = {}) =>
    track(new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0, ...o }))
  const mat = {
    gold: std('#FFC53D', { metalness: 0.8, roughness: 0.24, envMapIntensity: 1.7, side: THREE.DoubleSide }),
    goldGlow: std('#FFD15C', {
      metalness: 0.85, roughness: 0.2, envMapIntensity: 1.8, emissive: '#C77700', emissiveIntensity: 0.6,
      side: THREE.DoubleSide,
    }),
    baseWood: std('#7A4E1F', { roughness: 0.55 }),
    silver: std('#ECE8E1', { metalness: 0.6, roughness: 0.35, envMapIntensity: 1.4, side: THREE.DoubleSide }),
    silverBase: std('#BDB3A2', { roughness: 0.7 }),
    shelf: std('#C98E55', { roughness: 0.7 }),
    shelfDark: std('#A86F3C', { roughness: 0.75 }),
    cabinet: std('#E9A15E', { roughness: 0.7 }),
    door: std('#F6C690', { roughness: 0.65 }),
    knob: std('#FFF4E2', { roughness: 0.4 }),
    wallEdge: std('#EED7B8', { roughness: 0.9 }),
    skirting: std('#FFFDF8', { roughness: 0.6 }),
    frame: std('#FFFFFF', { roughness: 0.5 }),
    floorEdge: std('#B88A58', { roughness: 0.8 }),
    pedestal: std('#FFF7EA', { roughness: 0.55 }),
    pedestalTrim: std('#E8833A', { roughness: 0.55 }),
    pot: std('#D9774A', { roughness: 0.75 }),
    soil: std('#6B4A2E', { roughness: 1 }),
    leaf: std('#7CC157', { roughness: 0.6 }),
    leafDark: std('#5E9E3E', { roughness: 0.6 }),
    giftBox: std('#BCD6F7', { roughness: 0.55 }),
    ribbon: std('#E8833A', { roughness: 0.45 }),
    bunting: ['#E8833A', '#F4B942', '#7CC157', '#FFF4E2', '#9ED8F5'].map((c) =>
      std(c, { roughness: 0.8, side: THREE.DoubleSide })),
  }

  const tex = (c: HTMLCanvasElement) => {
    const t = track(new THREE.CanvasTexture(c))
    t.colorSpace = THREE.SRGBColorSpace
    t.anisotropy = aniso
    return t
  }

  const solids: THREE.Object3D[] = [] // block taps on things behind them
  const add = (mesh: THREE.Mesh, opts: { cast?: boolean; receive?: boolean; solid?: boolean } = {}) => {
    mesh.castShadow = opts.cast ?? true
    mesh.receiveShadow = opts.receive ?? true
    if (opts.solid) solids.push(mesh)
    scene.add(mesh)
    return mesh
  }
  const box = (w: number, h: number, d: number, m: THREE.Material | THREE.Material[],
               x: number, y: number, z: number) => {
    const mesh = new THREE.Mesh(track(new THREE.BoxGeometry(w, h, d)), m)
    mesh.position.set(x, y, z)
    return mesh
  }

  // ---------------------------------------------------------- the room
  // Floor: x from -5.25 to 5.4, z from -4.25 to 4.4, top at y = 0.
  {
    const [c, g] = canvas2d(512, 512)
    const tones = ['#DDB98A', '#D6B07F', '#E2C096', '#D2AD7C', '#DAB585']
    for (let row = 0; row < 8; row++) {
      const y = row * 64
      let x = -((row * 97) % 180)
      let i = row
      while (x < 512) {
        const w = 150 + ((i * 53) % 90)
        g.fillStyle = tones[(i + row) % tones.length]!
        g.fillRect(x, y, w, 64)
        g.fillStyle = 'rgba(120,80,40,0.10)'
        g.fillRect(x + 20, y + 24, w * 0.5, 2)
        g.fillStyle = '#B98F5E'
        g.fillRect(x, y, 2, 64)
        x += w
        i++
      }
      g.fillStyle = '#BC9262'
      g.fillRect(0, y, 512, 3)
    }
    const planks = tex(c)
    planks.wrapS = planks.wrapT = THREE.RepeatWrapping
    planks.repeat.set(10.65 / 3, 8.65 / 3)
    const top = std('#FFFFFF', { map: planks, roughness: 0.7 })
    const edge = mat.floorEdge
    const floor = box(10.65, 0.3, 8.65, [edge, edge, top, edge, edge, edge], 0.075, -0.15, 0.075)
    add(floor, { cast: false })
  }

  // Wallpaper: warm cream with small dots, the same on both walls.
  const [wc, wg] = canvas2d(256, 256)
  wg.fillStyle = '#FCE6CA'
  wg.fillRect(0, 0, 256, 256)
  for (const [x, y, col] of [[64, 64, '#F7D5AA'], [192, 192, '#F7D5AA'], [192, 64, '#E9EFD9'], [64, 192, '#E9EFD9']] as const) {
    wg.fillStyle = col
    wg.beginPath()
    wg.arc(x, y, 12, 0, Math.PI * 2)
    wg.fill()
  }
  const wallpaper = tex(wc)
  wallpaper.wrapS = wallpaper.wrapT = THREE.RepeatWrapping
  wallpaper.repeat.set(1 / 0.9, 1 / 0.9)
  const wallMat = std('#FFFFFF', { map: wallpaper, roughness: 0.95 })
  const wallMatSide = std('#FFF6EC', { map: wallpaper, roughness: 0.95 })
  const WALL_H = 3.8

  {
    // Back wall: inner face at z = -4.
    const shape = new THREE.Shape()
    shape.moveTo(-5.25, 0); shape.lineTo(5.4, 0); shape.lineTo(5.4, WALL_H); shape.lineTo(-5.25, WALL_H)
    shape.closePath()
    const geo = track(new THREE.ExtrudeGeometry(shape, { depth: 0.25, bevelEnabled: false }))
    const back = new THREE.Mesh(geo, [wallMat, mat.wallEdge])
    back.position.set(0, 0, -4.25)
    add(back, { cast: false, solid: true })

    // Left wall: inner face at x = -5, with a window.
    // Drawn in the wall's own (u, v), where u = -z, then turned to stand at x = -5.25.
    const left = new THREE.Shape()
    left.moveTo(-4.4, 0); left.lineTo(4.25, 0); left.lineTo(4.25, WALL_H); left.lineTo(-4.4, WALL_H)
    left.closePath()
    const hole = new THREE.Path()
    hole.moveTo(1.2, 1.25); hole.lineTo(3.4, 1.25); hole.lineTo(3.4, 2.85); hole.lineTo(1.2, 2.85)
    hole.closePath()
    left.holes.push(hole)
    const lgeo = track(new THREE.ExtrudeGeometry(left, { depth: 0.25, bevelEnabled: false }))
    const lwall = new THREE.Mesh(lgeo, [wallMatSide, mat.wallEdge])
    lwall.rotation.y = Math.PI / 2
    lwall.position.set(-5.25, 0, 0)
    add(lwall, { cast: false, solid: true })

    // Skirting boards.
    add(box(10.4, 0.14, 0.05, mat.skirting, 0.15, 0.07, -3.975), { cast: false })
    add(box(0.05, 0.14, 8.4, mat.skirting, -4.975, 0.07, 0.2), { cast: false })
  }

  // Window: sky, drifting clouds, white frame, a sill.
  const WIN = { z0: -3.4, z1: -1.2, y0: 1.25, y1: 2.85 }
  let clouds: THREE.CanvasTexture | null = null
  {
    const [c, g] = canvas2d(512, 384)
    const sky = g.createLinearGradient(0, 0, 0, 384)
    sky.addColorStop(0, '#8FD0F2')
    sky.addColorStop(1, '#DDF3FC')
    g.fillStyle = sky
    g.fillRect(0, 0, 512, 384)
    g.fillStyle = '#FFE08A'
    g.beginPath(); g.arc(400, 90, 54, 0, Math.PI * 2); g.fill()
    g.fillStyle = '#FFD166'
    g.beginPath(); g.arc(400, 90, 40, 0, Math.PI * 2); g.fill()
    g.fillStyle = '#A9D67E'
    g.beginPath(); g.ellipse(120, 400, 260, 110, 0, 0, Math.PI * 2); g.fill()
    g.fillStyle = '#8CC461'
    g.beginPath(); g.ellipse(430, 420, 230, 120, 0, 0, Math.PI * 2); g.fill()
    const skyMat = track(new THREE.MeshBasicMaterial({ map: tex(c), toneMapped: false }))
    const plane = new THREE.Mesh(track(new THREE.PlaneGeometry(3.4, 2.4)), skyMat)
    plane.rotation.y = Math.PI / 2
    plane.position.set(-5.7, 2.05, -2.3)
    scene.add(plane)

    const [cc, cg] = canvas2d(512, 256)
    cg.fillStyle = '#FFFFFF'
    for (const [x, y, s] of [[80, 70, 1], [300, 120, 1.3], [470, 50, 0.8]] as const) {
      cg.beginPath()
      cg.ellipse(x, y, 46 * s, 20 * s, 0, 0, Math.PI * 2)
      cg.ellipse(x - 26 * s, y + 6 * s, 26 * s, 16 * s, 0, 0, Math.PI * 2)
      cg.ellipse(x + 24 * s, y + 4 * s, 30 * s, 18 * s, 0, 0, Math.PI * 2)
      cg.fill()
    }
    clouds = tex(cc)
    clouds.wrapS = THREE.RepeatWrapping
    const cloudMat = track(new THREE.MeshBasicMaterial({ map: clouds, transparent: true, toneMapped: false, depthWrite: false }))
    const cplane = new THREE.Mesh(track(new THREE.PlaneGeometry(3.4, 1.7)), cloudMat)
    cplane.rotation.y = Math.PI / 2
    cplane.position.set(-5.65, 2.4, -2.3)
    scene.add(cplane)

    const fx = -5.0
    const zc = (WIN.z0 + WIN.z1) / 2
    const zw = WIN.z1 - WIN.z0
    const yc = (WIN.y0 + WIN.y1) / 2
    const yh = WIN.y1 - WIN.y0
    add(box(0.12, 0.1, zw + 0.24, mat.frame, fx + 0.02, WIN.y1 + 0.05, zc), { cast: false })
    add(box(0.12, yh + 0.2, 0.1, mat.frame, fx + 0.02, yc, WIN.z0 - 0.05), { cast: false })
    add(box(0.12, yh + 0.2, 0.1, mat.frame, fx + 0.02, yc, WIN.z1 + 0.05), { cast: false })
    add(box(0.06, yh, 0.06, mat.frame, -5.12, yc, zc), { cast: false })
    add(box(0.06, 0.06, zw, mat.frame, -5.12, yc, zc), { cast: false })
    // the sill sticks into the room, and trophies can stand on it
    add(box(0.42, 0.08, zw + 0.5, mat.frame, -4.82, WIN.y0 - 0.04, zc), { solid: true })
  }

  // Bunting along the top of the back wall.
  {
    const n = 15
    const points: THREE.Vector3[] = []
    for (let i = 0; i <= 40; i++) {
      const k = i / 40
      points.push(new THREE.Vector3(-4.9 + k * 10.1, 3.55 - Math.sin(k * Math.PI) * 0.25, -3.96))
    }
    const curve = new THREE.CatmullRomCurve3(points)
    const string = new THREE.Mesh(track(new THREE.TubeGeometry(curve, 60, 0.012, 6)), mat.shelfDark)
    scene.add(string)
    const tri = new THREE.Shape()
    tri.moveTo(-0.16, 0); tri.lineTo(0.16, 0); tri.lineTo(0, -0.34); tri.closePath()
    const triGeo = track(new THREE.ShapeGeometry(tri))
    for (let i = 0; i < n; i++) {
      const p = curve.getPointAt((i + 0.5) / n)
      const flag = new THREE.Mesh(triGeo, mat.bunting[i % mat.bunting.length]!)
      flag.position.set(p.x, p.y, -3.95)
      scene.add(flag)
    }
  }

  // A framed leaf picture on the back wall, right of the shelves.
  {
    const [c, g] = canvas2d(256, 300)
    g.fillStyle = '#F1F7EC'
    g.fillRect(0, 0, 256, 300)
    g.font = `150px ${EMOJI_FONT}`
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText('🌿', 128, 160)
    add(box(0.06, 1.05, 0.9, mat.shelf, -4.97, 2.15, 0.6), { cast: false })
    const pic = new THREE.Mesh(track(new THREE.PlaneGeometry(0.72, 0.86)),
      std('#FFFFFF', { map: tex(c), roughness: 0.9 }))
    pic.rotation.y = Math.PI / 2
    pic.position.set(-4.935, 2.15, 0.6)
    scene.add(pic)
  }

  // ------------------------------------------------------- furniture
  const surfaces: Surface[] = []
  const catchers: THREE.Object3D[] = []
  const catchMat = track(new THREE.MeshBasicMaterial({ visible: false }))
  const catchOn = (mesh: THREE.Object3D, s: Surface) => {
    mesh.userData.surface = s
    catchers.push(mesh)
  }
  const catchBox = (s: Surface, w: number, h: number, d: number, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(track(new THREE.BoxGeometry(w, h, d)), catchMat)
    m.position.set(x, y, z)
    m.visible = false
    scene.add(m)
    catchOn(m, s)
  }

  // The shelf unit on the back wall: a cupboard, two shelves, a top.
  const SH = { cx: 0, cz: -3.7, hw: 2.8, hd: 0.3 }
  const shelfTops = [0.9, 1.75, 2.6]
  const shelfSurface = (key: string, top: number): Surface =>
    ({ key, top, shape: 'rect', cx: SH.cx, cz: SH.cz, hw: SH.hw, hd: SH.hd, r: 0, floor: false })
  const [cabTop, midTop, topTop] = shelfTops as [number, number, number]
  const cupboard = shelfSurface('cupboard', cabTop)
  const middle = shelfSurface('middle', midTop)
  const upper = shelfSurface('upper', topTop)
  surfaces.push(cupboard, middle, upper)
  {
    const body = add(box(5.7, cabTop - 0.06, 0.6, mat.cabinet, 0, (cabTop - 0.06) / 2, SH.cz), { solid: true })
    catchOn(body, cupboard)
    for (let i = 0; i < 3; i++) {
      const x = -1.88 + i * 1.88
      add(box(1.7, 0.62, 0.03, mat.door, x, 0.43, SH.cz + 0.31), { solid: true })
      const knob = new THREE.Mesh(track(new THREE.SphereGeometry(0.045, 16, 12)), mat.knob)
      knob.position.set(x + (i === 1 ? 0 : i === 0 ? 0.65 : -0.65), 0.45, SH.cz + 0.35)
      add(knob)
    }
    for (const [s, top] of [[cupboard, cabTop], [middle, midTop], [upper, topTop]] as const) {
      const board = add(box(5.82, 0.08, 0.66, mat.shelf, 0, top - 0.04, SH.cz + 0.02), { solid: true })
      catchOn(board, s)
      catchBox(s, 5.6, 0.72, 0.8, 0, top + 0.36, SH.cz + 0.08)
    }
    // sides and top
    for (const x of [-2.93, 2.93]) {
      add(box(0.1, 3.3, 0.66, mat.shelfDark, x, 1.65, SH.cz + 0.02), { solid: true })
    }
    add(box(6.0, 0.1, 0.7, mat.shelfDark, 0, 3.35, SH.cz + 0.02), { solid: true })
    // a little garland of leaves along the top
    const leafGeo = track(new THREE.SphereGeometry(1, 12, 8))
    for (let i = 0; i < 18; i++) {
      const leaf = new THREE.Mesh(leafGeo, i % 3 === 0 ? mat.leafDark : mat.leaf)
      leaf.scale.set(0.11, 0.05, 0.06)
      leaf.position.set(-2.8 + i * 0.33, 3.43 + Math.sin(i * 1.3) * 0.02, SH.cz + 0.3)
      leaf.rotation.z = i % 2 ? 0.5 : -0.5
      add(leaf, { receive: false })
    }
  }

  // Window sill (a place to stand trophies too).
  const sill: Surface = {
    key: 'sill', top: WIN.y0, shape: 'rect', cx: -4.82, cz: (WIN.z0 + WIN.z1) / 2,
    hw: 0.21, hd: (WIN.z1 - WIN.z0) / 2 + 0.25, r: 0, floor: false,
  }
  surfaces.push(sill)
  catchBox(sill, 0.5, 0.7, WIN.z1 - WIN.z0 + 0.5, -4.8, WIN.y0 + 0.35, sill.cz)

  // Two pedestals.
  const pedestals: Surface[] = []
  for (const [key, x, z, h] of [['pedestal-left', -2.9, 1.7, 1.0], ['pedestal-right', 3.5, -1.1, 0.8]] as const) {
    const s: Surface = { key, top: h, shape: 'circle', cx: x, cz: z, hw: 0, hd: 0, r: 0.42, floor: false }
    pedestals.push(s)
    surfaces.push(s)
    const column = new THREE.Mesh(track(new THREE.CylinderGeometry(0.27, 0.3, h - 0.3, 32)), mat.pedestal)
    column.position.set(x, 0.14 + (h - 0.3) / 2, z)
    add(column, { solid: true })
    catchOn(column, s)
    for (const [r0, r1, hh, y, m] of [
      [0.44, 0.46, 0.08, 0.04, mat.pedestalTrim],
      [0.34, 0.38, 0.06, 0.11, mat.pedestal],
      [0.36, 0.3, 0.06, h - 0.17, mat.pedestal],
      [0.43, 0.43, 0.05, h - 0.115, mat.pedestalTrim],
      [0.46, 0.44, 0.09, h - 0.045, mat.pedestal],
    ] as const) {
      const ring = new THREE.Mesh(track(new THREE.CylinderGeometry(r0, r1, hh, 32)), m)
      ring.position.set(x, y, z)
      add(ring, { solid: true })
      catchOn(ring, s)
    }
    const m = new THREE.Mesh(track(new THREE.CylinderGeometry(0.47, 0.47, 0.7, 20)), catchMat)
    m.position.set(x, h + 0.35, z)
    m.visible = false
    scene.add(m)
    catchOn(m, s)
  }

  // The rug.
  {
    const [c, g] = canvas2d(512, 512)
    const rings = ['#E8833A', '#FDEBD8', '#F6B27A', '#FFF4E2', '#F4B942', '#FDEBD8', '#9CCB74', '#FFF7EA']
    rings.forEach((col, i) => {
      g.fillStyle = col
      g.beginPath()
      g.arc(256, 256, 256 - i * 30, 0, Math.PI * 2)
      g.fill()
    })
    const rug = new THREE.Mesh(track(new THREE.CircleGeometry(1, 72)),
      std('#FFFFFF', { map: tex(c), roughness: 1 }))
    rug.rotation.x = -Math.PI / 2
    rug.scale.set(2.3, 1.45, 1)
    rug.position.set(0.2, 0.006, 0.5)
    add(rug, { cast: false })
  }

  // The plant, in the back right corner.
  const PLANT = { x: -4.35, z: -0.55 }
  {
    const pot = new THREE.Mesh(track(new THREE.CylinderGeometry(0.34, 0.26, 0.55, 28)), mat.pot)
    pot.position.set(PLANT.x, 0.275, PLANT.z)
    add(pot, { solid: true })
    const rim = new THREE.Mesh(track(new THREE.TorusGeometry(0.34, 0.04, 10, 28)), mat.pot)
    rim.rotation.x = Math.PI / 2
    rim.position.set(PLANT.x, 0.55, PLANT.z)
    add(rim)
    const soil = new THREE.Mesh(track(new THREE.CircleGeometry(0.31, 24)), mat.soil)
    soil.rotation.x = -Math.PI / 2
    soil.position.set(PLANT.x, 0.53, PLANT.z)
    add(soil, { cast: false })
    const leafGeo = track(new THREE.SphereGeometry(1, 16, 12))
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2
      const tilt = 0.35 + (i % 3) * 0.18
      const len = 0.55 + (i % 2) * 0.2
      const leaf = new THREE.Mesh(leafGeo, i % 2 ? mat.leaf : mat.leafDark)
      leaf.scale.set(0.13, len, 0.05)
      const holder = new THREE.Group()
      holder.position.set(PLANT.x, 0.5, PLANT.z)
      holder.rotation.set(0, a, tilt)
      leaf.position.set(0, len * 0.9, 0)
      holder.add(leaf)
      leaf.castShadow = true
      scene.add(holder)
    }
  }

  // Everything that sits on the floor and so cannot be stood inside.
  const GIFT = { x: 4.45, z: -3.3 }
  const floorBlocks: ({ kind: 'circle'; x: number; z: number; r: number }
    | { kind: 'rect'; x0: number; x1: number; z0: number; z1: number })[] = [
    { kind: 'rect', x0: -3.2, x1: 3.2, z0: -4.5, z1: -3.3 },
    ...pedestals.map((p) => ({ kind: 'circle' as const, x: p.cx, z: p.cz, r: 0.5 + EDGE })),
    { kind: 'circle', x: PLANT.x, z: PLANT.z, r: 0.38 + EDGE },
  ]
  if (input.hiddenCount > 0) floorBlocks.push({ kind: 'circle', x: GIFT.x, z: GIFT.z, r: 0.38 + EDGE })

  const floor: Surface = {
    key: 'floor', top: 0, shape: 'rect', cx: 0.075, cz: 0.075, hw: 5.0, hd: 4.0, r: 0, floor: true,
  }
  surfaces.push(floor)
  {
    const m = new THREE.Mesh(track(new THREE.PlaneGeometry(40, 40)), catchMat)
    m.rotation.x = -Math.PI / 2
    m.visible = false
    scene.add(m)
    catchOn(m, floor)
  }

  // ------------------------------------------------------- the cups
  const cupGeo = (() => {
    const base = track(new THREE.BoxGeometry(0.38, 0.11, 0.3))
    base.translate(0, 0.055, 0)
    const plinth = track(new THREE.CylinderGeometry(0.11, 0.13, 0.05, 28))
    plinth.translate(0, 0.135, 0)
    const stem = track(new THREE.CylinderGeometry(0.03, 0.055, 0.13, 20))
    stem.translate(0, 0.225, 0)
    const knob = track(new THREE.SphereGeometry(0.048, 18, 12))
    knob.translate(0, 0.255, 0)
    const profile = [
      [0.0, 0.27], [0.07, 0.28], [0.13, 0.31], [0.172, 0.36], [0.195, 0.42], [0.205, 0.48],
      [0.212, 0.535], [0.226, 0.56], [0.212, 0.565], [0.195, 0.54], [0.182, 0.48], [0.165, 0.42],
      [0.12, 0.37], [0.0, 0.35],
    ].map(([x, y]) => new THREE.Vector2(x, y))
    const bowl = track(new THREE.LatheGeometry(profile, 48))
    const handleR = track(new THREE.TorusGeometry(0.08, 0.02, 10, 24, Math.PI * 1.05))
    handleR.rotateZ(-Math.PI / 2 - 0.03)
    handleR.translate(0.2, 0.445, 0)
    const handleL = track(new THREE.TorusGeometry(0.08, 0.02, 10, 24, Math.PI * 1.05))
    handleL.rotateZ(Math.PI / 2 + 0.03)
    handleL.translate(-0.2, 0.445, 0)
    const medal = track(new THREE.CircleGeometry(0.1, 40))
    medal.translate(0, 0.445, 0.207)
    const plaque = track(new THREE.PlaneGeometry(0.32, 0.075))
    plaque.translate(0, 0.055, 0.1505)
    const proxy = track(new THREE.CylinderGeometry(0.3, 0.3, 0.78, 12))
    proxy.translate(0, 0.39, 0)
    return { base, plinth, stem, knob, bowl, handleR, handleL, medal, plaque, proxy }
  })()

  const medalTexture = (emoji: string, gold: boolean, progress?: number) => {
    const [c, g] = canvas2d(160, 160)
    g.beginPath()
    g.arc(80, 80, 76, 0, Math.PI * 2)
    g.fillStyle = gold ? '#FFF3CC' : '#F1EDE6'
    g.fill()
    g.lineWidth = 8
    g.strokeStyle = gold ? '#C9850F' : '#BFB6A6'
    g.stroke()
    g.font = `88px ${EMOJI_FONT}`
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText(emoji, 80, 86)
    if (!gold) {
      // Grey and faded, like the flat room's "not yet" cups.
      const img = g.getImageData(0, 0, 160, 160)
      const d = img.data
      for (let i = 0; i < d.length; i += 4) {
        const y = 0.3 * d[i]! + 0.59 * d[i + 1]! + 0.11 * d[i + 2]!
        const v = y * 0.45 + 240 * 0.55
        d[i] = v; d[i + 1] = v; d[i + 2] = v
      }
      g.putImageData(img, 0, 0)
      if (progress !== undefined) {
        g.beginPath()
        g.arc(80, 80, 68, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.max(0.02, Math.min(1, progress)))
        g.lineWidth = 10
        g.lineCap = 'round'
        g.strokeStyle = '#7A9A5B'
        g.stroke()
      }
    }
    return tex(c)
  }

  const plaqueTexture = (name: string) => {
    const [c, g] = canvas2d(384, 90)
    roundRect(g, 3, 3, 378, 84, 16)
    g.fillStyle = '#F7D67A'
    g.fill()
    g.lineWidth = 5
    g.strokeStyle = '#B97510'
    g.stroke()
    let px = 46
    g.font = `600 ${px}px ${LABEL_FONT}`
    while (g.measureText(name).width > 340 && px > 20) {
      px -= 2
      g.font = `600 ${px}px ${LABEL_FONT}`
    }
    g.fillStyle = '#5A3A12'
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText(name, 192, 48)
    return tex(c)
  }

  const makeCup = (gold: boolean, emoji: string, name: string, progress?: number) => {
    const group = new THREE.Group()
    const metal = gold ? mat.gold : mat.silver
    const shiny: THREE.Mesh[] = []
    const part = (geo: THREE.BufferGeometry, m: THREE.Material, isShiny = false) => {
      const mesh = new THREE.Mesh(geo, m)
      mesh.castShadow = true
      mesh.receiveShadow = true
      group.add(mesh)
      if (isShiny) shiny.push(mesh)
      return mesh
    }
    part(cupGeo.base, gold ? mat.baseWood : mat.silverBase)
    for (const geo of [cupGeo.plinth, cupGeo.stem, cupGeo.knob, cupGeo.bowl, cupGeo.handleR, cupGeo.handleL]) {
      part(geo, metal, true)
    }
    const medalMap = medalTexture(emoji, gold, progress)
    const medal = part(cupGeo.medal, std('#FFFFFF', {
      map: medalMap, emissiveMap: medalMap, emissive: '#FFFFFF', emissiveIntensity: 0.45,
      roughness: 0.5, alphaTest: 0.5,
    }))
    medal.castShadow = false
    if (gold) {
      const plaque = part(cupGeo.plaque, std('#FFFFFF', { map: plaqueTexture(name), roughness: 0.4, metalness: 0.2 }))
      plaque.castShadow = false
    }
    return { group, shiny }
  }

  // Twinkles on the gold, made once and shared.
  const starTex = (() => {
    const [c, g] = canvas2d(64, 64)
    const grad = g.createRadialGradient(32, 32, 0, 32, 32, 30)
    grad.addColorStop(0, 'rgba(255,255,255,1)')
    grad.addColorStop(0.25, 'rgba(255,248,220,0.7)')
    grad.addColorStop(1, 'rgba(255,240,200,0)')
    g.fillStyle = grad
    g.beginPath()
    g.moveTo(32, 0); g.quadraticCurveTo(36, 28, 64, 32); g.quadraticCurveTo(36, 36, 32, 64)
    g.quadraticCurveTo(28, 36, 0, 32); g.quadraticCurveTo(28, 28, 32, 0)
    g.fill()
    return tex(c)
  })()
  const sparkles: { sprite: THREE.Sprite; phase: number; size: number }[] = []

  // A rounded label that always faces you.
  const makeLabel = (text: string, height: number, look: { bg: string; border: string; color: string },
                     onTop: boolean) => {
    const px = 56
    const [, mg] = canvas2d(8, 8)
    mg.font = `600 ${px}px ${LABEL_FONT}`
    const w = Math.ceil(mg.measureText(text).width + px * 1.3)
    const h = Math.ceil(px * 1.6)
    const [c, g] = canvas2d(w, h)
    g.font = `600 ${px}px ${LABEL_FONT}`
    roundRect(g, 4, 4, w - 8, h - 8, (h - 8) / 2)
    g.fillStyle = look.bg
    g.fill()
    g.lineWidth = 6
    g.strokeStyle = look.border
    g.stroke()
    g.fillStyle = look.color
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText(text, w / 2, h / 2 + 3)
    const t = new THREE.CanvasTexture(c)
    t.colorSpace = THREE.SRGBColorSpace
    const m = new THREE.SpriteMaterial({ map: t, depthTest: !onTop, transparent: true, toneMapped: false })
    const sprite = new THREE.Sprite(m)
    sprite.scale.set((height * w) / h, height, 1)
    sprite.renderOrder = onTop ? 20 : 5
    sprite.center.set(0.5, 0)
    return { sprite, dispose: () => { t.dispose(); m.dispose() } }
  }

  // --------------------------------------------- where things stand
  const trophies: Trophy[] = []
  const entries = new Map<string, Entry>()
  const proxies: THREE.Object3D[] = []

  const proxyFor = (group: THREE.Group, key: string, scale = 1) => {
    const p = new THREE.Mesh(cupGeo.proxy, catchMat)
    p.visible = false
    p.scale.setScalar(scale)
    p.userData.key = key
    group.add(p)
    proxies.push(p)
  }

  const clampTo = (s: Surface, x: number, z: number): [number, number] => {
    if (s.shape === 'circle') {
      const dx = x - s.cx
      const dz = z - s.cz
      const max = Math.max(0, s.r - EDGE)
      const d = Math.hypot(dx, dz)
      return d <= max ? [x, z] : [s.cx + (dx / d) * max, s.cz + (dz / d) * max]
    }
    const mx = Math.max(0, s.hw - EDGE)
    const mz = Math.max(0, s.hd - EDGE)
    return [
      Math.min(s.cx + mx, Math.max(s.cx - mx, x)),
      Math.min(s.cz + mz, Math.max(s.cz - mz, z)),
    ]
  }
  const contains = (s: Surface, x: number, z: number, slack = 0.05) => {
    if (s.shape === 'circle') return Math.hypot(x - s.cx, z - s.cz) <= s.r + slack
    return Math.abs(x - s.cx) <= s.hw + slack && Math.abs(z - s.cz) <= s.hd + slack
  }
  /** On the floor, step out of anything standing there. */
  const avoidBlocks = (x: number, z: number): [number, number] => {
    for (let pass = 0; pass < 3; pass++) {
      for (const b of floorBlocks) {
        if (b.kind === 'circle') {
          const dx = x - b.x
          const dz = z - b.z
          const d = Math.hypot(dx, dz)
          if (d < b.r) {
            const ux = d > 1e-4 ? dx / d : 0
            const uz = d > 1e-4 ? dz / d : 1
            x = b.x + ux * b.r
            z = b.z + uz * b.r
          }
        } else if (x > b.x0 - EDGE && x < b.x1 + EDGE && z > b.z0 - EDGE && z < b.z1 + EDGE) {
          const options: [number, number, number][] = [
            [Math.abs(z - (b.z1 + EDGE)), x, b.z1 + EDGE],
            [Math.abs(x - (b.x0 - EDGE)), b.x0 - EDGE, z],
            [Math.abs(x - (b.x1 + EDGE)), b.x1 + EDGE, z],
          ]
          options.sort((a, b2) => a[0] - b2[0])
          const best = options[0]!
          x = best[1]
          z = best[2]
        }
      }
      ;[x, z] = clampTo(floor, x, z)
    }
    return [x, z]
  }
  const settle = (s: Surface, x: number, z: number): [number, number] => {
    const [cx, cz] = clampTo(s, x, z)
    return s.floor ? avoidBlocks(cx, cz) : [cx, cz]
  }
  const isFree = (s: Surface, x: number, z: number, except: Trophy | null, placed: Trophy[] = trophies) =>
    placed.every((t) => t === except || t.surface !== s || Math.hypot(t.x - x, t.z - z) >= GAP)

  /** The nearest free spot on the same surface; failing that, where it was dropped. */
  const freeSpot = (s: Surface, x: number, z: number, except: Trophy | null): [number, number] => {
    const [sx, sz] = settle(s, x, z)
    if (isFree(s, sx, sz, except)) return [sx, sz]
    for (let r = 0.1; r < 3; r += 0.1) {
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2
        const [cx, cz] = settle(s, x + Math.cos(a) * r, z + Math.sin(a) * r)
        if (isFree(s, cx, cz, except)) return [cx, cz]
      }
    }
    return [sx, sz]
  }

  const shelfOrder = [middle, cupboard, upper]
  /** Spaces for trophies beyond the shelves: the pedestals, then rows on the floor. */
  const spareSpots = (): [Surface, number, number][] => {
    const out: [Surface, number, number][] = pedestals.map((p) => [p, p.cx, p.cz])
    for (let z = -2.6; z <= 3.8; z += 0.7) {
      for (let x = -4.3; x <= 4.9; x += 0.7) {
        const [ax, az] = avoidBlocks(x, z)
        if (Math.abs(ax - x) < 1e-6 && Math.abs(az - z) < 1e-6) out.push([floor, x, z])
      }
    }
    return out
  }

  /** The tidy arrangement: shelves shared out evenly, oldest first, centred. */
  const tidySpots = (n: number): [Surface, number, number][] => {
    const out: [Surface, number, number][] = []
    const onShelves = Math.min(n, PER_SHELF * shelfOrder.length)
    const used = Math.ceil(onShelves / PER_SHELF)
    for (let i = 0; i < used; i++) {
      const s = shelfOrder[i]!
      const k = Math.floor(onShelves / used) + (i < onShelves % used ? 1 : 0)
      for (let j = 0; j < k; j++) out.push([s, s.cx + (j - (k - 1) / 2) * SHELF_STEP, s.cz])
    }
    const spare = spareSpots()
    for (let i = 0; out.length < n; i++) {
      const spot = spare[i % Math.max(1, spare.length)] ?? [floor, 0, 0]
      out.push(spot)
    }
    return out
  }

  /** Every spot on every shelf, middle outwards, then the spares: for newcomers. */
  const newcomerSpots = (): [Surface, number, number][] => {
    const out: [Surface, number, number][] = []
    for (const s of shelfOrder) {
      for (let j = 0; j < PER_SHELF; j++) {
        const step = Math.ceil(j / 2) * (j % 2 ? -1 : 1)
        out.push([s, s.cx + step * SHELF_STEP, s.cz])
      }
    }
    return out.concat(spareSpots())
  }

  const placeGroup = (t: Trophy, lift = 0) => {
    t.group.position.set(t.x, t.surface.top + lift, t.z)
    t.group.rotation.y = t.rot
  }

  // Earned trophies, oldest first.
  const foundSorted = [...input.found].sort((a, b) =>
    a.awarded_at.localeCompare(b.awarded_at) || a.code.localeCompare(b.code))
  const saved = input.layout
  const anySaved = foundSorted.some((b) => saved[b.code])
  const tidy0 = tidySpots(foundSorted.length)

  foundSorted.forEach((b, i) => {
    const { group, shiny } = makeCup(true, b.emoji, b.name)
    group.scale.setScalar(TROPHY_SCALE)
    const item: Item = { kind: 'found', ...b }
    const t: Trophy = { code: b.code, item, group, shiny, surface: floor, x: 0, z: 0, rot: 0 }
    const spot = saved[b.code]
    let placed = false
    if (spot) {
      const [x, y, z, rot] = spot
      const s = surfaces.find((c) => Math.abs(c.top - y) < 0.12 && contains(c, x, z, 0.3))
      if (s) {
        const [fx, fz] = settle(s, x, z)
        Object.assign(t, { surface: s, x: fx, z: fz, rot })
        placed = true
      }
    }
    if (!placed && !anySaved) {
      const [s, x, z] = tidy0[i]!
      Object.assign(t, { surface: s, x, z, rot: 0 })
      placed = true
    }
    if (placed) trophies.push(t)
    t.group.userData.pending = !placed
    scene.add(group)
    entries.set(b.code, { key: b.code, item, group, trophy: t, height: 0.6 * TROPHY_SCALE })
    proxyFor(group, b.code)
    for (const [sx, sy, sz, size] of [[0.25, 0.6, 0.08, 0.15], [-0.24, 0.3, 0.12, 0.11]] as const) {
      const sprite = new THREE.Sprite(track(new THREE.SpriteMaterial({
        map: starTex, transparent: true, depthWrite: false, toneMapped: false,
      })))
      sprite.position.set(sx, sy, sz)
      sprite.scale.setScalar(size)
      group.add(sprite)
      sparkles.push({ sprite, phase: Math.random() * Math.PI * 2, size })
    }
  })
  // Trophies with no saved spot (new since the last arrangement) take the
  // first free shelf spot.
  for (const entry of entries.values()) {
    const t = entry.trophy
    if (!t || !t.group.userData.pending) continue
    const spot = newcomerSpots().find(([s, x, z]) => isFree(s, x, z, null))
      ?? tidy0[trophies.length % Math.max(1, tidy0.length)] ?? [floor, 0, 0]
    const [s, x, z] = spot
    Object.assign(t, { surface: s, x, z, rot: 0 })
    t.group.userData.pending = false
    trophies.push(t)
  }
  trophies.forEach((t) => placeGroup(t))

  // Coming soon: silver cups on their own shelves at the right of the back
  // wall, not movable.
  const towards = input.towards
  const labels: { dispose: () => void }[] = []
  if (towards.length) {
    const perRow = 3
    const rows = Math.ceil(towards.length / perRow)
    const xc = 4.25
    const len = 2.05
    const z = -3.78
    const rowY = (r: number) => 1.15 + r * 0.82
    for (let r = 0; r < rows; r++) {
      const y = rowY(r)
      add(box(len, 0.07, 0.44, mat.shelf, xc, y - 0.035, z), { solid: true })
      for (const x of [xc - len / 2 + 0.22, xc + len / 2 - 0.22]) {
        add(box(0.05, 0.18, 0.36, mat.shelfDark, x, y - 0.15, z - 0.03))
      }
      const inRow = towards.slice(r * perRow, r * perRow + perRow)
      inRow.forEach((b, j) => {
        const { group } = makeCup(false, b.emoji, b.name, b.target ? b.now / b.target : 0)
        const x = xc + (j - (inRow.length - 1) / 2) * 0.66
        group.position.set(x, y, z)
        group.rotation.y = -0.15
        group.scale.setScalar(0.92)
        scene.add(group)
        const key = `towards:${b.code}`
        entries.set(key, { key, item: { kind: 'towards', ...b }, group, trophy: null, height: 0.56 })
        proxyFor(group, key)
        const lab = makeLabel(`${b.now}/${b.target}`, 0.14, { bg: '#FFFFFF', border: '#B9D39E', color: '#4A6135' }, false)
        lab.sprite.position.set(x, y + 0.6, z + 0.05)
        scene.add(lab.sprite)
        labels.push(lab)
      })
    }
    // the sign
    const [c, g] = canvas2d(512, 128)
    roundRect(g, 4, 4, 504, 120, 28)
    g.fillStyle = '#FFF7EA'
    g.fill()
    g.lineWidth = 8
    g.strokeStyle = '#7A9A5B'
    g.stroke()
    g.font = `600 64px ${LABEL_FONT}`
    g.fillStyle = '#4A6135'
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText('Coming soon', 256, 68)
    const sign = new THREE.Mesh(track(new THREE.PlaneGeometry(1.5, 0.375)),
      std('#FFFFFF', { map: tex(c), roughness: 0.8 }))
    sign.position.set(xc, rowY(rows) - 0.05, -3.97)
    scene.add(sign)
  }

  // Hidden ones: one wrapped present on the floor.
  if (input.hiddenCount > 0) {
    const gift = new THREE.Group()
    const s = 0.5
    const part = (m: THREE.Mesh) => {
      m.castShadow = true
      m.receiveShadow = true
      gift.add(m)
    }
    part(box(s, s * 0.82, s, mat.giftBox, 0, s * 0.41, 0))
    part(box(s + 0.05, 0.12, s + 0.05, mat.giftBox, 0, s * 0.82 + 0.05, 0))
    part(box(0.09, s * 0.82 + 0.13, s + 0.065, mat.ribbon, 0, (s * 0.82 + 0.13) / 2, 0))
    part(box(s + 0.065, s * 0.82 + 0.13, 0.09, mat.ribbon, 0, (s * 0.82 + 0.13) / 2, 0))
    const loopGeo = track(new THREE.TorusGeometry(0.09, 0.03, 10, 24))
    for (const side of [-1, 1]) {
      const loop = new THREE.Mesh(loopGeo, mat.ribbon)
      loop.position.set(side * 0.08, s * 0.82 + 0.19, 0)
      loop.rotation.set(0, 0, side * 0.6)
      part(loop)
    }
    const [c, g] = canvas2d(128, 128)
    g.font = `700 96px ${LABEL_FONT}`
    g.fillStyle = '#1D4ED8'
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillText('?', 64, 70)
    const q = new THREE.Mesh(track(new THREE.PlaneGeometry(0.22, 0.22)),
      track(new THREE.MeshStandardMaterial({ map: tex(c), transparent: true, roughness: 0.6 })))
    for (const [x, z, ry] of [[-0.14, s / 2 + 0.002, 0], [s / 2 + 0.002, -0.14, Math.PI / 2]] as const) {
      const face = q.clone()
      face.position.set(x, s * 0.36, z)
      face.rotation.y = ry
      gift.add(face)
    }
    gift.position.set(GIFT.x, 0, GIFT.z)
    gift.rotation.y = -0.3
    scene.add(gift)
    entries.set('hidden', { key: 'hidden', item: { kind: 'hidden', count: input.hiddenCount }, group: gift, trophy: null, height: 0.62 })
    proxyFor(gift, 'hidden', 1.1)
    if (input.hiddenCount > 1) {
      const lab = makeLabel(`×${input.hiddenCount}`, 0.17, { bg: '#FFFFFF', border: '#C6DAF6', color: '#1D4ED8' }, false)
      lab.sprite.position.set(GIFT.x, 0.82, GIFT.z)
      scene.add(lab.sprite)
      labels.push(lab)
    }
  }

  // The ring under whatever is chosen or carried.
  const halo = new THREE.Mesh(
    track(new THREE.RingGeometry(0.25, 0.33, 48)),
    track(new THREE.MeshBasicMaterial({ color: '#E8833A', transparent: true, opacity: 0.6, depthWrite: false, toneMapped: false })),
  )
  halo.rotation.x = -Math.PI / 2
  halo.visible = false
  halo.renderOrder = 4
  scene.add(halo)
  const haloMat = halo.material as THREE.MeshBasicMaterial
  let nameLabel: { sprite: THREE.Sprite; dispose: () => void } | null = null

  // ------------------------------------------------------- camera
  const view = { userMoved: false }
  let dirty = true
  let shadowDirty = true
  const markDirty = () => { dirty = true }

  // ------------------------------------------------- pointer handling
  // Registered BEFORE OrbitControls, so pressing on a trophy can switch the
  // camera off before it starts to turn.
  const ray = new THREE.Raycaster()
  const ndc = new THREE.Vector2()
  const setRay = (clientX: number, clientY: number) => {
    const r = canvas.getBoundingClientRect()
    ndc.set(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1)
    ray.setFromCamera(ndc, camera)
  }
  const pickables = [...proxies, ...solids]
  const pick = (clientX: number, clientY: number): Entry | null => {
    setRay(clientX, clientY)
    const hit = ray.intersectObjects(pickables, false)[0]
    const key = hit?.object.userData.key as string | undefined
    return key ? entries.get(key) ?? null : null
  }

  let selected: Entry | null = null
  const select = (entry: Entry | null, notify = true) => {
    selected = entry
    nameLabel?.sprite.removeFromParent()
    nameLabel?.dispose()
    nameLabel = null
    if (entry && entry.item.kind !== 'hidden') {
      const look = entry.item.kind === 'found'
        ? { bg: '#FFFFFF', border: '#E8833A', color: '#15202B' }
        : { bg: '#FFFFFF', border: '#B9D39E', color: '#31404E' }
      nameLabel = makeLabel(`${entry.item.emoji} ${entry.item.name}`, 0.26, look, true)
      scene.add(nameLabel.sprite)
    }
    if (notify) events.onSelect(entry ? entry.item : null)
    markDirty()
  }

  const layoutJson = () => {
    const out: Layout = {}
    const r3 = (n: number) => Math.round(n * 1000) / 1000
    for (const t of trophies) out[t.code] = [r3(t.x), r3(t.surface.top), r3(t.z), r3(t.rot)]
    return JSON.stringify(out)
  }

  // Little animations; with reduced motion they jump straight to the end.
  const tweens: { t0: number; dur: number; step: (k: number) => void; done?: () => void }[] = []
  const animate = (dur: number, step: (k: number) => void, done?: () => void, delay = 0) => {
    if (reducedMotion) {
      step(1)
      done?.()
      dirty = shadowDirty = true
      return
    }
    tweens.push({ t0: performance.now() + delay, dur, step, done })
  }

  let press: { id: number; x: number; y: number; entry: Entry | null; dragging: boolean; ox: number; oy: number } | null = null
  let carried: Trophy | null = null

  const glow = (t: Trophy, on: boolean) => {
    for (const m of t.shiny) m.material = on ? mat.goldGlow : mat.gold
  }

  const startCarry = (t: Trophy, clientX: number, clientY: number) => {
    carried = t
    // Hold it by where the finger is, not by its base, so it does not jump.
    const base = new THREE.Vector3(t.x, t.surface.top, t.z).project(camera)
    const r = canvas.getBoundingClientRect()
    const bx = ((base.x + 1) / 2) * r.width + r.left
    const by = ((1 - base.y) / 2) * r.height + r.top
    if (press) {
      press.ox = bx - clientX
      press.oy = by - clientY
    }
    glow(t, true)
    placeGroup(t, LIFT)
    haloMat.opacity = 0.9
    canvas.style.cursor = 'grabbing'
    if (selected !== entries.get(t.code)) select(entries.get(t.code) ?? null)
    dirty = shadowDirty = true
  }

  const moveCarry = (clientX: number, clientY: number) => {
    const t = carried
    if (!t || !press) return
    setRay(clientX + press.ox, clientY + press.oy)
    const hit = ray.intersectObjects(catchers, false)[0]
    const s = hit?.object.userData.surface as Surface | undefined
    if (!hit || !s) return
    const [x, z] = settle(s, hit.point.x, hit.point.z)
    t.surface = s
    t.x = x
    t.z = z
    placeGroup(t, LIFT)
    dirty = shadowDirty = true
  }

  const dropCarry = () => {
    const t = carried
    if (!t) return
    carried = null
    const [x, z] = freeSpot(t.surface, t.x, t.z, t)
    const fromX = t.x
    const fromZ = t.z
    t.x = x
    t.z = z
    glow(t, false)
    haloMat.opacity = 0.6
    canvas.style.cursor = 'grab'
    animate(180, (k) => {
      if (carried === t) return
      const e = easeOut(k)
      t.group.position.set(fromX + (x - fromX) * e, t.surface.top + LIFT * (1 - e), fromZ + (z - fromZ) * e)
    })
    sfx.tap()
    events.onLayout(layoutJson())
    dirty = shadowDirty = true
  }

  const onDown = (e: PointerEvent) => {
    if (press) {
      // A second finger: let it pinch or turn the room instead.
      if (!press.dragging) { press = null; orbit.enabled = true }
      return
    }
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const entry = pick(e.clientX, e.clientY)
    press = { id: e.pointerId, x: e.clientX, y: e.clientY, entry, dragging: false, ox: 0, oy: 0 }
    if (entry?.trophy) {
      orbit.enabled = false
      try { canvas.setPointerCapture(e.pointerId) } catch { /* fine */ }
    }
  }
  const onMove = (e: PointerEvent) => {
    if (!press) {
      if (e.pointerType === 'mouse') {
        const entry = pick(e.clientX, e.clientY)
        canvas.style.cursor = entry?.trophy ? 'grab' : entry ? 'pointer' : 'default'
      }
      return
    }
    if (e.pointerId !== press.id) return
    const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y)
    if (!press.dragging && press.entry?.trophy && moved > 6) {
      press.dragging = true
      startCarry(press.entry.trophy, press.x, press.y)
    }
    if (press.dragging) moveCarry(e.clientX, e.clientY)
  }
  const onUp = (e: PointerEvent) => {
    if (!press || e.pointerId !== press.id) return
    const p = press
    press = null
    orbit.enabled = true
    if (p.dragging) {
      dropCarry()
      return
    }
    if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < 8) {
      if (p.entry) sfx.tap()
      select(p.entry)
    }
  }
  const onCancel = (e: PointerEvent) => {
    if (!press || e.pointerId !== press.id) return
    const p = press
    press = null
    orbit.enabled = true
    if (p.dragging) dropCarry()
  }
  const onLeave = () => { if (!press) canvas.style.cursor = 'default' }
  canvas.addEventListener('pointerdown', onDown)
  canvas.addEventListener('pointermove', onMove)
  canvas.addEventListener('pointerup', onUp)
  canvas.addEventListener('pointercancel', onCancel)
  canvas.addEventListener('pointerleave', onLeave)

  const orbit = new OrbitControls(camera, canvas)
  orbit.enableDamping = !reducedMotion
  orbit.dampingFactor = 0.12
  orbit.enablePan = false
  orbit.rotateSpeed = 0.6
  orbit.zoomSpeed = 0.8
  orbit.minPolarAngle = 0.62
  orbit.maxPolarAngle = 1.42
  orbit.minAzimuthAngle = -0.22
  orbit.maxAzimuthAngle = 0.95
  orbit.addEventListener('change', markDirty)
  orbit.addEventListener('start', () => { view.userMoved = true })

  // Frame the room so it fits, whatever the shape of the screen.
  const wideKeys = [
    [-2.95, 0.3, -3.4], [2.95, 0.3, -3.4], [-2.95, 3.5, -3.4], [2.95, 3.5, -3.4],
    [5.2, 2.0, -3.8], [-5, 2.85, -3.4], [-5, 1.2, -1.2], [-2.9, 0.5, 1.7], [3.5, 0.65, -1.1],
  ].map(([x, y, z]) => new THREE.Vector3(x, y, z))
  const narrowKeys = [
    [-2.6, 0.9, -3.4], [2.6, 0.9, -3.4], [-2.6, 3.4, -3.4], [2.6, 3.4, -3.4], [0, 0, 1.0],
  ].map(([x, y, z]) => new THREE.Vector3(x, y, z))
  const fit = () => {
    const narrow = camera.aspect < 1
    const target = narrow ? new THREE.Vector3(0.1, 1.45, -1.6) : new THREE.Vector3(-0.5, 1.5, -1.6)
    const dir = new THREE.Vector3().setFromSphericalCoords(1, narrow ? 1.1 : 1.17, narrow ? 0.14 : 0.32)
    const keys = narrow ? narrowKeys : wideKeys
    const v = new THREE.Vector3()
    let lo = 3
    let hi = 40
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2
      camera.position.copy(target).addScaledVector(dir, mid)
      camera.lookAt(target)
      camera.updateMatrixWorld()
      const ok = keys.every((k) => {
        v.copy(k).project(camera)
        return Math.abs(v.x) <= 0.97 && Math.abs(v.y) <= 0.93 && v.z < 1
      })
      if (ok) hi = mid
      else lo = mid
    }
    camera.position.copy(target).addScaledVector(dir, hi)
    orbit.target.copy(target)
    orbit.minDistance = Math.min(4, hi * 0.5)
    orbit.maxDistance = hi * 1.25
    orbit.update()
  }

  const resize = () => {
    const w = host.clientWidth
    const h = host.clientHeight
    if (!w || !h) return
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    if (!view.userMoved) fit()
    dirty = true
  }
  const observer = new ResizeObserver(resize)
  observer.observe(host)
  resize()

  // ------------------------------------------------------ the loop
  let raf = 0
  let frame = 0
  let disposed = false
  const loop = (now: number) => {
    raf = requestAnimationFrame(loop)
    orbit.update()
    for (let i = tweens.length - 1; i >= 0; i--) {
      const tw = tweens[i]!
      const k = Math.min(1, Math.max(0, (now - tw.t0) / tw.dur))
      tw.step(k)
      if (k >= 1) {
        tweens.splice(i, 1)
        tw.done?.()
      }
      dirty = shadowDirty = true
    }
    frame++
    const idle = !reducedMotion && frame % 2 === 0
    if (idle) {
      const s = now / 1000
      for (const sp of sparkles) {
        const k = Math.max(0, Math.sin(s * 1.6 + sp.phase))
        sp.sprite.material.opacity = k
        sp.sprite.scale.setScalar(sp.size * (0.6 + 0.4 * k))
      }
      if (clouds) clouds.offset.x = (s * 0.006) % 1
    }
    if (selected) {
      const g = selected.group
      const baseY = selected.trophy ? selected.trophy.surface.top : g.position.y
      halo.visible = true
      halo.position.set(g.position.x, baseY + 0.012, g.position.z)
      nameLabel?.sprite.position.set(g.position.x, g.position.y + selected.height + 0.12, g.position.z)
    } else {
      halo.visible = false
    }
    if (dirty || idle) {
      if (shadowDirty) {
        renderer.shadowMap.needsUpdate = true
        shadowDirty = false
      }
      renderer.render(scene, camera)
      dirty = false
    }
  }
  if (reducedMotion) {
    for (const sp of sparkles) sp.sprite.material.opacity = 0.75
  }
  raf = requestAnimationFrame(loop)

  const onLost = (e: Event) => {
    e.preventDefault()
    if (!disposed) events.onLost()
  }
  canvas.addEventListener('webglcontextlost', onLost)

  // --------------------------------------------------------- handle
  return {
    select: (code) => {
      select(code ? entries.get(code) ?? null : null, false)
    },
    turn: () => {
      const t = selected?.trophy
      if (!t || carried) return
      const from = t.rot
      const to = from + Math.PI / 4
      t.rot = to
      animate(260, (k) => { if (carried !== t) t.group.rotation.y = from + (to - from) * easeInOut(k) })
      sfx.tap()
      events.onLayout(layoutJson())
    },
    tidy: () => {
      const spots = tidySpots(trophies.length)
      const byAge = [...trophies].sort((a, b) => {
        const ia = foundSorted.findIndex((f) => f.code === a.code)
        const ib = foundSorted.findIndex((f) => f.code === b.code)
        return ia - ib
      })
      byAge.forEach((t, i) => {
        const [s, x, z] = spots[i]!
        const from = t.group.position.clone()
        const fromRot = t.group.rotation.y
        const toRot = 0
        t.surface = s
        t.x = x
        t.z = z
        t.rot = 0
        animate(520, (k) => {
          if (carried === t) return
          const e = easeInOut(k)
          t.group.position.set(
            from.x + (x - from.x) * e,
            from.y + (s.top - from.y) * e + Math.sin(Math.PI * k) * 0.5,
            from.z + (z - from.z) * e,
          )
          t.group.rotation.y = fromRot + (toRot - fromRot) * e
        }, undefined, i * 45)
      })
      events.onLayout('{}')
    },
    dispose: () => {
      disposed = true
      cancelAnimationFrame(raf)
      observer.disconnect()
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerup', onUp)
      canvas.removeEventListener('pointercancel', onCancel)
      canvas.removeEventListener('pointerleave', onLeave)
      canvas.removeEventListener('webglcontextlost', onLost)
      orbit.dispose()
      nameLabel?.dispose()
      for (const l of labels) l.dispose()
      for (const d of disposables) d.dispose()
      scene.clear()
      renderer.dispose()
      renderer.forceContextLoss()
      canvas.remove()
    },
  }
}
